// ─────────────────────────────────────────────────────────────────────────────
// Phase 10 — Canonical Identity (PURE).
//
// Single source of truth for "who is the authenticated user, and which
// family-graph node represents them?". See IDENTITY_ARCHITECTURE.md for the
// full contract. This module is pure (no React, no Supabase, no side
// effects) so it can be consumed by:
//
//   • the React hook at `src/lib/identity/useCanonicalIdentity.ts`
//   • non-React code (scripts, tests, future edge-function helpers)
//
// HARD RULES (enforced by this module):
//   1. Payment status (`plan` / `isPaid`) is INDEPENDENT of family-graph
//      membership. A paid user with no canonical member node is still paid.
//   2. This module NEVER mutates anything. It only DERIVES a binding-state
//      snapshot from inputs the caller already has.
//   3. The canonical member is the SINGLE row whose `claimed_by_user_id`
//      matches the auth user. If multiple rows are claimed by the same
//      user (corrupt state), the FIRST match is returned and a `warnings`
//      entry is added to the snapshot. The DB has a unique partial index
//      that should make this impossible at the storage layer.
//   4. `viewerCandidates` is non-empty ONLY when the canonical member is
//      null AND name-matched placeholder rows exist that the user could
//      plausibly claim. The IdentityBindingModal consumes this list.
//   5. `requiresIdentityPrompt` is true when the user is authenticated,
//      no canonical member is bound, and personalized tree features
//      (YOU badge, viewer-relative kinship labels, Center on Me) are
//      therefore unavailable until the user confirms or creates their
//      identity row.
// ─────────────────────────────────────────────────────────────────────────────

import type { GraphMember } from '@/contexts/FamilyGraphContext';
import type { Plan, Role } from '@/contexts/AuthContext';

/**
 * Discriminated state representing the relationship between the auth user
 * and the family-graph. See §4 of IDENTITY_ARCHITECTURE.md for the full
 * state-machine definition.
 */
export type BindingState =
  | 'loading'         // Auth or graph still resolving
  | 'unauthenticated' // No auth user
  | 'bound'           // Canonical member found
  | 'ambiguous'       // No canonical member, but candidate matches exist
  | 'unbound_paid'    // Authenticated + paid, no candidate, no canonical
  | 'unbound_free'    // Authenticated + free, no candidate, no canonical
  | 'error';          // Auth resolved but profile fetch failed

/**
 * Inputs the derivation needs. The hook in
 * `useCanonicalIdentity.ts` collects these from `useAuth()` and
 * `useFamilyGraph()`. Edge-function helpers and tests can construct them
 * directly from raw row data.
 */
export interface CanonicalIdentityInputs {
  authLoading: boolean;
  authUserId: string | null;
  authEmail: string | null;
  authDisplayName: string | null;

  profileError: string | null;
  profileLoading: boolean;
  plan: Plan;
  role: Role;
  isPaid: boolean;
  isStaff: boolean;
  invitedBy: string | null;

  graphLoading: boolean;
  members: GraphMember[];
  effectiveOwnerUserId: string | null;
  /**
   * The viewer member id that `FamilyGraphContext.refresh()` resolved via
   * its standard claim lookup + the four-strategy auto-bind ladder. The
   * canonical layer trusts this value as the primary source for the
   * "bound" state — it never re-runs the ladder itself.
   */
  viewerMemberIdFromGraph: string | null;
  /**
   * The list of unclaimed name-match candidate rows that the graph layer
   * surfaced when the auto-bind ladder couldn't pick exactly one row.
   */
  viewerCandidatesFromGraph: GraphMember[];
}

export interface CanonicalIdentitySnapshot {
  bindingState: BindingState;

  // Auth surface
  authUserId: string | null;
  authEmail: string | null;
  authDisplayName: string | null;

  // Profile / billing surface (INDEPENDENT of graph binding)
  plan: Plan;
  role: Role;
  isPaid: boolean;
  isStaff: boolean;
  invitedBy: string | null;
  profileError: string | null;

  // Graph surface
  effectiveOwnerUserId: string | null;
  /** Single row whose `claimed_by_user_id === authUserId`, or null. */
  canonicalMemberId: string | null;
  canonicalMember: GraphMember | null;
  /** Name-match placeholders the IdentityBindingModal can offer. */
  viewerCandidates: GraphMember[];

  /**
   * True when the user is authenticated AND has no canonical member bound
   * AND personalized features should therefore be gated behind a "tell us
   * who you are in the tree" prompt. False when bound, loading, or
   * unauthenticated.
   */
  requiresIdentityPrompt: boolean;

  /**
   * True when the user is authenticated AND paid AND no canonical member
   * is bound. Surfaces the specific "you paid, but you haven't added
   * yourself to the tree yet" experience without affecting billing.
   */
  isPaidButUnbound: boolean;

  /**
   * Phase 11: True when the canonical layer believes the auto-bind ladder
   * could safely pick a row WITHOUT user disambiguation (i.e. exactly
   * ONE viewer candidate exists, no claim conflict, no strong duplicate
   * risk). FALSE when ambiguous, when the user is already bound, or
   * when any guard would reject the auto-bind.
   *
   * The auto-bind ladder in `FamilyGraphContext.refresh()` does NOT
   * read this field today — it runs its own checks. The field is
   * surfaced so future migrations of binding pathways have a single
   * pre-computed answer they can route through `evaluateBindingSafety`.
   */
  canAutoBind: boolean;

  /**
   * Phase 11: Highest duplicate-confidence found by scanning the
   * canonical member (when bound) or the user's display-name (when
   * unbound) against the rest of the loaded graph. Surfaces a "this
   * person may already exist on the tree under a different row" hint
   * for diagnostic UI.
   *
   * Always 'none' when unbound and `viewerCandidates.length === 0` —
   * there is nothing to compare against.
   */
  duplicateRisk: 'none' | 'possible' | 'likely' | 'strong';

  /**
   * Non-fatal advisories the consumer may want to surface in dev or in
   * SystemHealth. Examples:
   *   - 'multiple-canonical-rows' — DB returned >1 row claimed by the
   *     same user (should be impossible; investigate).
   *   - 'profile-error' — profile row failed to load; UI should show
   *     retry instead of treating user as free.
   */
  warnings: string[];
}


/**
 * Pure derivation. Given a snapshot of auth + profile + graph state,
 * produce the canonical identity result. Always returns a fully-populated
 * object — never throws, never returns null.
 */

export function deriveCanonicalIdentity(
  inputs: CanonicalIdentityInputs,
): CanonicalIdentitySnapshot {
  const warnings: string[] = [];

  // ── EARLY-BOUND SHORT-CIRCUIT (May 2026 hydration-timing repair) ──────
  //
  // PROBLEM (root cause of "User Connection stuck on graph-loading after
  // a successful claim"):
  //   The previous derivation gated EVERYTHING on `inputs.authLoading`
  //   first, returning bindingState='loading' the moment auth was still
  //   resolving. But the graph layer can already have a fully-bound
  //   viewer member at that point (the row was claimed earlier in this
  //   session, or `bindViewerToMember()` just resolved and set
  //   viewerMemberId locally, OR a claimed row is already visible in
  //   `members[]` with claimedByUserId === authUserId). Returning
  //   'loading' in that state caused the SystemStatusPanel + Tree
  //   Center Diagnostic to keep saying "Connecting…" indefinitely even
  //   though identity binding had actually completed.
  //
  // FIX:
  //   If we already have BOTH (a) a known authUserId AND (b) a
  //   resolvable canonical member (either the graph-layer's
  //   viewerMemberIdFromGraph points at it, OR the loaded members list
  //   contains a row claimed by this user), return 'bound' immediately
  //   — auth bootstrap may still be in flight for OTHER fields (session
  //   refresh, profile fetch), but identity binding is unambiguously
  //   resolved and personalized surfaces are safe to render.
  //
  //   This is purely a SHORT-CIRCUIT on the existing canonical-member
  //   resolution below — never mutates anything, never bypasses the
  if (inputs.authUserId) {
    let earlyCanonical: GraphMember | null = null;
    if (inputs.viewerMemberIdFromGraph) {
      earlyCanonical =
        inputs.members.find(
          (m) => m.id === inputs.viewerMemberIdFromGraph,
        ) ?? null;
    }
    if (!earlyCanonical) {
      // ── BROADENED CANONICAL LOOKUP (May 2026 — null-claimedBy hydration repair) ──
      // SQL truth: a row is "mine" when
      //     claimed_by_user_id = authUserId
      //  OR (owner_user_id = authUserId AND profile_state = 'claimed').
      //
      // The previous predicate only matched the first clause. When the
      // hydrated GraphMember's `claimedByUserId` came back null (stale
      // members[] snapshot, intermediate optimistic write, or RLS
      // column-level visibility filtering the claimed_by_user_id column
      // on a particular cross-graph SELECT), the canonical layer
      // returned bindingState='loading' / 'unbound' forever even though
      // the DB row clearly identified the viewer via the OR-clause.
      //
      // Adding the second clause as a fallback closes that hydration
      // gap WITHOUT touching the DB, RLS, claims, or any write path.
      // Pure read-side derivation fix.
      const claimedHere = inputs.members.filter(
        (m) =>
          m.claimedByUserId === inputs.authUserId ||
          (m.ownerUserId === inputs.authUserId && m.profileState === 'claimed'),
      );
      if (claimedHere.length > 0) {
        earlyCanonical = claimedHere[0];
        if (claimedHere.length > 1) warnings.push('multiple-canonical-rows');
      }
    }
    if (earlyCanonical) {
      const duplicateRisk = computeDuplicateRisk(
        inputs,
        earlyCanonical,
        inputs.members.filter(
          (m) =>
            m.claimedByUserId === inputs.authUserId ||
            (m.ownerUserId === inputs.authUserId && m.profileState === 'claimed'),
        ),
      );
      return baseSnapshot(inputs, warnings, {
        bindingState: 'bound',
        canonicalMemberId: earlyCanonical.id,
        canonicalMember: earlyCanonical,
        viewerCandidates: [],
        requiresIdentityPrompt: false,
        isPaidButUnbound: false,
        canAutoBind: false,
        duplicateRisk,
      });
    }
  }

  if (inputs.authLoading) {
    return baseSnapshot(inputs, warnings, {
      bindingState: 'loading',
      canonicalMemberId: null,
      canonicalMember: null,
      viewerCandidates: [],
      requiresIdentityPrompt: false,
      isPaidButUnbound: false,
      canAutoBind: false,
      duplicateRisk: 'none',
    });
  }

  // ── 2. Unauthenticated ────────────────────────────────────────────────
  if (!inputs.authUserId) {
    return baseSnapshot(inputs, warnings, {
      bindingState: 'unauthenticated',
      canonicalMemberId: null,
      canonicalMember: null,
      viewerCandidates: [],
      requiresIdentityPrompt: false,
      isPaidButUnbound: false,
      canAutoBind: false,
      duplicateRisk: 'none',
    });
  }

  // ── 3. Profile-load error ────────────────────────────────────────────
  if (inputs.profileError) {
    warnings.push('profile-error');
    return baseSnapshot(inputs, warnings, {
      bindingState: 'error',
      canonicalMemberId: null,
      canonicalMember: null,
      viewerCandidates: [],
      requiresIdentityPrompt: false,
      isPaidButUnbound: false,
      canAutoBind: false,
      duplicateRisk: 'none',
    });
  }

  // ── 4. Resolve canonical member ──────────────────────────────────────
  const claimedRows = inputs.members.filter(
    (m) => m.claimedByUserId === inputs.authUserId,
  );
  if (claimedRows.length > 1) {
    warnings.push('multiple-canonical-rows');
  }

  let canonicalMemberId: string | null = null;
  let canonicalMember: GraphMember | null = null;
  if (inputs.viewerMemberIdFromGraph) {
    const fromGraph = inputs.members.find(
      (m) => m.id === inputs.viewerMemberIdFromGraph,
    ) ?? null;
    if (fromGraph) {
      canonicalMember = fromGraph;
      canonicalMemberId = fromGraph.id;
    }
  }
  if (!canonicalMember && claimedRows.length > 0) {
    canonicalMember = claimedRows[0];
    canonicalMemberId = claimedRows[0].id;
  }


  // ── Phase 11: derive duplicate risk ──────────────────────────────────
  // We DON'T re-run the heavy `findDuplicateMatches` engine here (that
  // belongs to the duplicate-guardrail flag). We compute a conservative
  // hint from already-loaded state.
  const duplicateRisk = computeDuplicateRisk(
    inputs,
    canonicalMember,
    claimedRows,
  );

  // ── 5. Bound state ───────────────────────────────────────────────────
  if (canonicalMemberId && canonicalMember) {
    return baseSnapshot(inputs, warnings, {
      bindingState: 'bound',
      canonicalMemberId,
      canonicalMember,
      viewerCandidates: [],
      requiresIdentityPrompt: false,
      isPaidButUnbound: false,
      canAutoBind: false, // already bound — auto-bind not applicable
      duplicateRisk,
    });
  }

  // ── 6. Ambiguous (candidates available) ──────────────────────────────
  const safeCandidates = (inputs.viewerCandidatesFromGraph ?? []).filter(
    (m) => !m.claimedByUserId,
  );
  if (safeCandidates.length > 0) {
    // Phase 11 contract: NEVER auto-pick when ambiguous. canAutoBind is
    // true ONLY when exactly one candidate exists AND no other claim by
    // this user exists AND no duplicate risk fires.
    const canAutoBind =
      safeCandidates.length === 1 &&
      claimedRows.length === 0 &&
      duplicateRisk !== 'strong';
    return baseSnapshot(inputs, warnings, {
      bindingState: 'ambiguous',
      canonicalMemberId: null,
      canonicalMember: null,
      viewerCandidates: safeCandidates,
      requiresIdentityPrompt: true,
      isPaidButUnbound: inputs.isPaid,
      canAutoBind,
      duplicateRisk,
    });
  }

  // ── 7. Unbound (no candidates) ───────────────────────────────────────
  if (inputs.isPaid || inputs.isStaff) {
    return baseSnapshot(inputs, warnings, {
      bindingState: 'unbound_paid',
      canonicalMemberId: null,
      canonicalMember: null,
      viewerCandidates: [],
      requiresIdentityPrompt: true,
      isPaidButUnbound: true,
      canAutoBind: false,
      duplicateRisk,
    });
  }
  return baseSnapshot(inputs, warnings, {
    bindingState: 'unbound_free',
    canonicalMemberId: null,
    canonicalMember: null,
    viewerCandidates: [],
    requiresIdentityPrompt: true,
    isPaidButUnbound: false,
    canAutoBind: false,
    duplicateRisk,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────

const norm = (s: string | null | undefined): string =>
  (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const tokens = (s: string | null | undefined): string[] =>
  norm(s).split(' ').filter((t) => t.length >= 2);

/**
 * Conservative, self-contained duplicate-risk hint. Does not use the
 * `duplicateGuardrail`-flagged engine — the canonical layer needs to
 * report this consistently regardless of feature flags.
 */
function computeDuplicateRisk(
  inputs: CanonicalIdentityInputs,
  canonical: GraphMember | null,
  claimedRows: GraphMember[],
): 'none' | 'possible' | 'likely' | 'strong' {
  // Multiple claimed rows → STRONG (data-corruption signal).
  if (claimedRows.length > 1) return 'strong';

  const probeName = canonical?.name ?? inputs.authDisplayName ?? '';
  const probeTokens = tokens(probeName);
  if (probeTokens.length < 2) return 'none';
  const [first, last] = [probeTokens[0], probeTokens[probeTokens.length - 1]];

  let exactNameDupes = 0;
  let firstLastDupes = 0;
  for (const m of inputs.members) {
    if (!m || !m.id) continue;
    if (canonical && m.id === canonical.id) continue;
    if (m.approvalStatus === 'rejected') continue;
    const t = tokens(m.name);
    if (t.length < 2) continue;
    if (norm(m.name) === norm(probeName)) {
      exactNameDupes += 1;
      continue;
    }
    if (t[0] === first && t[t.length - 1] === last) {
      firstLastDupes += 1;
    }
  }
  if (exactNameDupes >= 1) return 'likely';
  if (firstLastDupes >= 1) return 'possible';
  return 'none';
}

function baseSnapshot(
  inputs: CanonicalIdentityInputs,
  warnings: string[],
  partial: Pick<
    CanonicalIdentitySnapshot,
    | 'bindingState'
    | 'canonicalMemberId'
    | 'canonicalMember'
    | 'viewerCandidates'
    | 'requiresIdentityPrompt'
    | 'isPaidButUnbound'
    | 'canAutoBind'
    | 'duplicateRisk'
  >,
): CanonicalIdentitySnapshot {
  return {
    ...partial,
    authUserId: inputs.authUserId,
    authEmail: inputs.authEmail,
    authDisplayName: inputs.authDisplayName,
    plan: inputs.plan,
    role: inputs.role,
    isPaid: inputs.isPaid,
    isStaff: inputs.isStaff,
    invitedBy: inputs.invitedBy,
    profileError: inputs.profileError,
    effectiveOwnerUserId: inputs.effectiveOwnerUserId,
    warnings,
  };
}


/**
 * Convenience predicate — true when the snapshot represents a state
 * where personalized viewer-relative features (YOU badge, kinship
 * labels, Center on Me) may be rendered.
 */
export function isPersonalizedViewReady(
  snapshot: Pick<CanonicalIdentitySnapshot, 'bindingState'>,
): boolean {
  return snapshot.bindingState === 'bound';
}

/**
 * Convenience predicate — true when the snapshot represents a paid
 * account whose personalized features are temporarily gated awaiting
 * an identity binding. Header / Pricing / Account can use this to show
 * the "you're paid; tell us who you are in the tree" affordance without
 * ever showing an Upgrade CTA.
 */
export function shouldPromptIdentityBinding(
  snapshot: Pick<
    CanonicalIdentitySnapshot,
    'bindingState' | 'isPaidButUnbound'
  >,
): boolean {
  return (
    snapshot.bindingState === 'ambiguous' ||
    snapshot.bindingState === 'unbound_paid' ||
    snapshot.bindingState === 'unbound_free' ||
    snapshot.isPaidButUnbound
  );
}
